import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, FormControlLabel, IconButton, InputLabel,
  MenuItem, Paper, Select, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Tooltip, Typography
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import Edit from "@mui/icons-material/Edit";
import FacebookIcon from "@mui/icons-material/Facebook";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import PhotoFilterIcon from "@mui/icons-material/PhotoFilter";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1, padding: theme.spacing(2), margin: theme.spacing(1),
  overflowY: "auto", borderRadius: theme.shape.borderRadius, boxShadow: theme.shadows[1],
}));

const STANDARD_EVENTS = ["Lead","InitiateCheckout","Purchase","CompleteRegistration","Contact","ViewContent","AddToCart"];

// ── Pixel Modal ────────────────────────────────────────────────────────────────
const PixelModal = ({ open, onClose, pixel }) => {
  const [name, setName] = useState("");
  const [pixelId, setPixelId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [testEventCode, setTestEventCode] = useState("");
  const [active, setActive] = useState(true);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [products, setProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(pixel?.name || "");
    setPixelId(pixel?.pixelId || "");
    setAccessToken(pixel?.accessToken || "");
    setTestEventCode(pixel?.testEventCode || "");
    setActive(pixel?.active !== false);
    const linked = pixel?.linkedProducts?.[0];
    setSelectedProductId(linked ? String(linked.id) : "");
    api.get("/products").then(r => setProducts(r.data || [])).catch(() => setProducts([]));
  }, [open, pixel]);

  const handleSave = async () => {
    if (!pixelId.trim()) { toast.error("Pixel ID e obrigatorio"); return; }
    if (!accessToken.trim()) { toast.error("Access Token e obrigatorio"); return; }
    setSaving(true);
    try {
      await api.post("/tracking/pixels", {
        id: pixel?.id,
        name: name || pixelId,
        pixelId, accessToken, testEventCode, active,
        productId: selectedProductId ? Number(selectedProductId) : null
      });
      toast.success(pixel ? "Pixel atualizado!" : "Pixel adicionado!");
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1877F2", color: "#fff", py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PhotoFilterIcon />
          <Typography fontWeight={700}>{pixel ? "Editar Pixel" : "Adicionar Pixel"}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <TextField fullWidth size="small" label="Nome do pixel (identificacao)" value={name}
            onChange={e => setName(e.target.value)} placeholder="Ex: Pixel ComunicaCast, Pixel Campanha 2" autoFocus />
          <TextField fullWidth size="small" label="Pixel ID *" value={pixelId}
            onChange={e => setPixelId(e.target.value)} placeholder="Ex: 1234567890123456" />
          <TextField fullWidth size="small" label="Access Token (Conversions API) *" value={accessToken}
            onChange={e => setAccessToken(e.target.value)} type="password" placeholder="EAABsbCS..." />
          <TextField fullWidth size="small" label="Codigo de teste (opcional)" value={testEventCode}
            onChange={e => setTestEventCode(e.target.value)} placeholder="Ex: TEST79605"
            helperText="Cole o codigo do painel Testar Eventos do Facebook" />
          <FormControl fullWidth size="small">
            <InputLabel>Produto vinculado (opcional)</InputLabel>
            <Select value={selectedProductId} label="Produto vinculado (opcional)"
              onChange={e => setSelectedProductId(e.target.value)}>
              <MenuItem value=""><em>Nenhum produto</em></MenuItem>
              {products.map(p => <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch checked={active} onChange={e => setActive(e.target.checked)} color="primary" />
            <Typography variant="body2">{active ? "Ativo" : "Inativo"}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="small"
          sx={{ backgroundColor: "#1877F2", "&:hover": { backgroundColor: "#1565C0" } }}>
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Event Modal ────────────────────────────────────────────────────────────────
const EventModal = ({ open, onClose, event, tags }) => {
  const theme = useTheme();
  const [eventName, setEventName] = useState("Lead");
  const [eventValue, setEventValue] = useState("");
  const [currency, setCurrency] = useState("BRL");
  const [active, setActive] = useState(true);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedPixelId, setSelectedPixelId] = useState("");
  const [pixels, setPixels] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEventName(event?.eventName || "Lead");
    setEventValue(event?.eventValue !== null && event?.eventValue !== undefined ? String(event.eventValue) : "");
    setCurrency(event?.currency || "BRL");
    setActive(event?.active !== false);
    setSelectedTags(event?.eventTags ? event.eventTags.map(et => et.tagId) : []);
    setSelectedPixelId(event?.trackingPixelId ? String(event.trackingPixelId) : "");
    api.get("/tracking/pixels").then(r => setPixels(r.data || [])).catch(() => setPixels([]));
  }, [open, event]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/tracking/events", {
        id: event?.id || undefined,
        eventName,
        eventValue: eventValue !== "" ? Number(eventValue) : null,
        currency, active, tagIds: selectedTags,
        trackingPixelId: selectedPixelId ? Number(selectedPixelId) : null
      });
      toast.success(event ? "Evento atualizado!" : "Evento criado!");
      onClose(true);
    } catch (err) { toastError(err); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: theme.palette.primary.main, color: "#fff", py: 1.5 }}>
        <Typography fontWeight={700}>{event ? "Editar Evento" : "Novo Evento"}</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Evento do Facebook</InputLabel>
            <Select value={eventName} label="Evento do Facebook" onChange={e => setEventName(e.target.value)}>
              {STANDARD_EVENTS.map(ev => <MenuItem key={ev} value={ev}>{ev}</MenuItem>)}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField fullWidth size="small" label="Valor da conversao (opcional)"
              value={eventValue} onChange={e => setEventValue(e.target.value)} type="number" placeholder="Ex: 97.00" />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Moeda</InputLabel>
              <Select value={currency} label="Moeda" onChange={e => setCurrency(e.target.value)}>
                <MenuItem value="BRL">BRL</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <FormControl fullWidth size="small">
            <InputLabel>Vincular etiquetas (opcional)</InputLabel>
            <Select multiple value={selectedTags} label="Vincular etiquetas (opcional)"
              onChange={e => setSelectedTags(e.target.value)}
              renderValue={sel => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {sel.map(id => {
                    const t = tags.find(x => x.id === id);
                    return t ? <Chip key={id} label={t.name} size="small" sx={{ backgroundColor: t.color, color: "#fff", fontSize: "0.7rem" }} /> : null;
                  })}
                </Box>
              )}>
              {tags.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: t.color }} />
                    {t.name}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Pixel especifico (opcional)</InputLabel>
            <Select value={selectedPixelId} label="Pixel especifico (opcional)" onChange={e => setSelectedPixelId(e.target.value)}>
              <MenuItem value=""><em>Todos os pixels ativos</em></MenuItem>
              {pixels.filter(p => p.active).map(px => (
                <MenuItem key={px.id} value={String(px.id)}>
                  {px.name || px.pixelId}
                  {px.linkedProducts?.length > 0 && " (" + px.linkedProducts.map(p => p.name).join(", ") + ")"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch checked={active} onChange={e => setActive(e.target.checked)} color="primary" />
            <Typography variant="body2">{active ? "Ativo" : "Inativo"}</Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={() => onClose(false)} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} size="small">
          {saving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Fire Event Modal (from conversation) ──────────────────────────────────────
export const FireEventModal = ({ open, onClose, contactId, companyEvents }) => {
  const [eventName, setEventName] = useState("");
  const [value, setValue] = useState("");
  const [currency, setCurrency] = useState("BRL");
  const [firing, setFiring] = useState(false);
  const [pixels, setPixels] = useState([]);
  const [selectedPixelId, setSelectedPixelId] = useState("");

  useEffect(() => {
    if (!open) return;
    setEventName(companyEvents?.[0]?.eventName || "Lead");
    setValue("");
    setSelectedPixelId("");
    api.get("/tracking/pixels").then(r => setPixels(r.data || [])).catch(() => setPixels([]));
  }, [open, companyEvents]);

  const handleFire = async () => {
    if (!eventName) { toast.error("Selecione um evento"); return; }
    setFiring(true);
    try {
      await api.post("/tracking/fire", {
        contactId, eventName,
        value: value !== "" ? Number(value) : undefined,
        currency,
        trackingPixelId: selectedPixelId ? Number(selectedPixelId) : undefined
      });
      toast.success("Evento disparado para o Facebook!");
      onClose();
    } catch (err) { toastError(err); }
    finally { setFiring(false); }
  };

  const activePixels = pixels.filter(p => p.active);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ backgroundColor: "#1877F2", color: "#fff", py: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FacebookIcon />
          <Typography fontWeight={700}>Disparar Evento Facebook</Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Evento</InputLabel>
            <Select value={eventName} label="Evento" onChange={e => setEventName(e.target.value)}>
              {STANDARD_EVENTS.map(ev => <MenuItem key={ev} value={ev}>{ev}</MenuItem>)}
            </Select>
          </FormControl>
          {activePixels.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Pixel / Produto (opcional)</InputLabel>
              <Select value={selectedPixelId} label="Pixel / Produto (opcional)" onChange={e => setSelectedPixelId(e.target.value)}>
                <MenuItem value=""><em>Todos os pixels ativos</em></MenuItem>
                {activePixels.map(px => (
                  <MenuItem key={px.id} value={String(px.id)}>
                    {px.name || px.pixelId}
                    {px.linkedProducts?.length > 0 && ` (${px.linkedProducts.map(p => p.name).join(", ")})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField fullWidth size="small" label="Valor (opcional)" value={value}
              onChange={e => setValue(e.target.value)} type="number" placeholder="Ex: 97.00" />
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel>Moeda</InputLabel>
              <Select value={currency} label="Moeda" onChange={e => setCurrency(e.target.value)}>
                <MenuItem value="BRL">BRL</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: "#f5f5f5" }}>
        <Button onClick={onClose} size="small">Cancelar</Button>
        <Button variant="contained" onClick={handleFire} disabled={firing} size="small"
          sx={{ backgroundColor: "#1877F2", "&:hover": { backgroundColor: "#1565C0" } }}>
          {firing ? "Disparando..." : "Disparar evento"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Tracking Page ─────────────────────────────────────────────────────────
const Tracking = () => {
  const [pixels, setPixels] = useState([]);
  const [events, setEvents] = useState([]);
  const [tags, setTags] = useState([]);
  const [pixelModalOpen, setPixelModalOpen] = useState(false);
  const [selectedPixel, setSelectedPixel] = useState(null);
  const [confirmPixelOpen, setConfirmPixelOpen] = useState(false);
  const [deletingPixel, setDeletingPixel] = useState(null);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [confirmEventOpen, setConfirmEventOpen] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [{ data: px }, { data: evts }, { data: tgs }] = await Promise.all([
        api.get("/tracking/pixels"),
        api.get("/tracking/events"),
        api.get("/tags/list"),
      ]);
      setPixels(px || []);
      setEvents(evts || []);
      setTags(tgs || []);
    } catch (err) { toastError(err); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleDeletePixel = async () => {
    try {
      await api.delete("/tracking/pixels/" + deletingPixel.id);
      toast.success("Pixel removido");
      setPixels(prev => prev.filter(p => p.id !== deletingPixel.id));
    } catch (err) { toastError(err); }
    setDeletingPixel(null);
    setConfirmPixelOpen(false);
  };

  const handleDeleteEvent = async () => {
    try {
      await api.delete("/tracking/events/" + deletingEvent.id);
      toast.success("Evento excluido");
      setEvents(prev => prev.filter(e => e.id !== deletingEvent.id));
    } catch (err) { toastError(err); }
    setDeletingEvent(null);
    setConfirmEventOpen(false);
  };

  const activePixels = pixels.filter(p => p.active).length;

  return (
    <MainContainer>
      <ConfirmationModal title="Remover pixel" open={confirmPixelOpen} onClose={setConfirmPixelOpen} onConfirm={handleDeletePixel}>
        Tem certeza? Os eventos deixarao de ser enviados para este pixel.
      </ConfirmationModal>
      <ConfirmationModal title="Excluir evento" open={confirmEventOpen} onClose={setConfirmEventOpen} onConfirm={handleDeleteEvent}>
        Tem certeza? O evento deixara de ser disparado.
      </ConfirmationModal>
      <PixelModal open={pixelModalOpen}
        onClose={r => { setPixelModalOpen(false); setSelectedPixel(null); if (r) loadData(); }}
        pixel={selectedPixel} />
      <EventModal open={eventModalOpen}
        onClose={r => { setEventModalOpen(false); setSelectedEvent(null); if (r) loadData(); }}
        event={selectedEvent} tags={tags} />

      <MainHeader>
        <Title>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FacebookIcon sx={{ color: "#1877F2" }} />
            Rastreamento Facebook
            {activePixels > 0 && <Chip label={activePixels + " pixel" + (activePixels > 1 ? "s" : "") + " ativo" + (activePixels > 1 ? "s" : "")} size="small" color="primary" sx={{ fontSize: "0.7rem", backgroundColor: "#1877F2" }} />}
          </Box>
        </Title>
        <MainHeaderButtonsWrapper>
          <Tooltip title="Novo evento">
            <Button variant="outlined" color="primary" onClick={() => { setSelectedEvent(null); setEventModalOpen(true); }} sx={{ height: 40, mr: 1 }} startIcon={<FlashOnIcon />}>
              Evento
            </Button>
          </Tooltip>
          <Tooltip title="Adicionar pixel">
            <Button variant="contained" onClick={() => { setSelectedPixel(null); setPixelModalOpen(true); }} sx={{ height: 40, backgroundColor: "#1877F2", "&:hover": { backgroundColor: "#1565C0" } }} startIcon={<PhotoFilterIcon />}>
              Pixel
            </Button>
          </Tooltip>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <MainPaper variant="outlined">
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
          <PhotoFilterIcon sx={{ color: "#1877F2", fontSize: "1.2rem" }} />
          Pixels configurados ({pixels.length})
        </Typography>
        <Table size="small" sx={{ mb: 3 }}>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Pixel ID</TableCell>
              <TableCell>Produto vinculado</TableCell>
              <TableCell align="center">Cod. Teste</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pixels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                    <FacebookIcon sx={{ fontSize: "2rem", color: "#1877F2", opacity: 0.4 }} />
                    <Typography variant="body2" color="textSecondary">
                      Nenhum pixel configurado. Adicione pixels e vincule-os a produtos.
                    </Typography>
                    <Button size="small" variant="contained" startIcon={<PhotoFilterIcon />}
                      onClick={() => { setSelectedPixel(null); setPixelModalOpen(true); }}
                      sx={{ backgroundColor: "#1877F2", mt: 1 }}>
                      Adicionar primeiro pixel
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : pixels.map(px => (
              <TableRow key={px.id} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{px.name || "-"}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{px.pixelId}</Typography></TableCell>
                <TableCell>
                  {px.linkedProducts?.length > 0 ? (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {px.linkedProducts.map(p => <Chip key={p.id} label={p.name} size="small" color="primary" variant="outlined" sx={{ fontSize: "0.65rem" }} />)}
                    </Box>
                  ) : <Typography variant="caption" color="textSecondary">-</Typography>}
                </TableCell>
                <TableCell align="center">
                  {px.testEventCode ? <Chip label={px.testEventCode} size="small" color="warning" variant="outlined" sx={{ fontSize: "0.65rem" }} /> : <Typography variant="caption" color="textSecondary">-</Typography>}
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={!!px.active}
                    onChange={async () => {
                      await api.post("/tracking/pixels", { id: px.id, pixelId: px.pixelId, accessToken: px.accessToken, testEventCode: px.testEventCode, name: px.name, active: !px.active }).catch(toastError);
                      loadData();
                    }} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setSelectedPixel(px); setPixelModalOpen(true); }}><Edit color="info" /></IconButton>
                  <IconButton size="small" onClick={() => { setDeletingPixel(px); setConfirmPixelOpen(true); }}><DeleteOutline color="error" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <FlashOnIcon sx={{ color: "#ff6f00", fontSize: "1.2rem" }} />
            Eventos configurados ({events.length})
          </Typography>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Evento</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell>Pixel</TableCell>
              <TableCell>Etiquetas vinculadas</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      Nenhum evento configurado. Crie eventos e vincule etiquetas para disparar automaticamente.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : events.map(ev => (
              <TableRow key={ev.id} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{ev.eventName}</Typography></TableCell>
                <TableCell align="center">
                  {ev.eventValue ? <Chip label={Number(ev.eventValue).toFixed(2) + " " + ev.currency} size="small" color="success" variant="outlined" sx={{ fontSize: "0.7rem" }} /> : <Typography variant="caption" color="textSecondary">-</Typography>}
                </TableCell>
                <TableCell>
                  {ev.trackingPixel ? (
                    <Chip label={ev.trackingPixel.name || ev.trackingPixel.pixelId} size="small" sx={{ backgroundColor: "#1877F2", color: "#fff", fontSize: "0.65rem" }} />
                  ) : <Typography variant="caption" color="textSecondary">Todos</Typography>}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {ev.eventTags?.length > 0 ? ev.eventTags.map(et => (
                      <Chip key={et.id} label={et.tag ? et.tag.name : et.tagId} size="small"
                        sx={{ backgroundColor: et.tag ? et.tag.color : "#999", color: "#fff", fontSize: "0.65rem" }} />
                    )) : <Typography variant="caption" color="textSecondary">Sem etiquetas</Typography>}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Switch size="small" checked={!!ev.active}
                    onChange={async () => { await api.post("/tracking/events", { id: ev.id, active: !ev.active }).catch(toastError); loadData(); }} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => { setSelectedEvent(ev); setEventModalOpen(true); }}><Edit color="info" /></IconButton>
                  <IconButton size="small" onClick={() => { setDeletingEvent(ev); setConfirmEventOpen(true); }}><DeleteOutline color="error" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </MainPaper>
    </MainContainer>
  );
};

export default Tracking;
